import React, { useState, useEffect } from "react";
import {
  Settings, Shield, Users, ChevronDown, ChevronUp, Plus, Trash2,
  Edit, Save, X, Check, AlertTriangle, RefreshCw, GripVertical,
  UserCheck, UserX, Crown, BookOpen, DollarSign, ChevronRight,
  Info, ToggleLeft, ToggleRight, ArrowUp, ArrowDown, Zap,
  Lock, Star, Award,
} from "lucide-react";
import axios from "axios";

const API = "https://api.jamboricg.com/api/majanga";

// ─── helpers ──────────────────────────────────────────────────────────────────
const api = {
  get:   (url)        => axios.get(`${API}${url}`).then(r => r.data),
  post:  (url, data)  => axios.post(`${API}${url}`, data).then(r => r.data),
  patch: (url, data)  => axios.patch(`${API}${url}`, data).then(r => r.data),
  del:   (url)        => axios.delete(`${API}${url}`).then(r => r.data),
};

const ROLE_ICONS = {
  member:      Crown,
  chairperson: Award,
  secretary:   BookOpen,
  treasurer:   DollarSign,
  vice_chair:  Star,
  custom:      Zap,
};

const RoleBadge = ({ roleType }) => {
  const colors = {
    member:      "bg-slate-100 text-slate-600",
    chairperson: "bg-amber-100 text-amber-700",
    secretary:   "bg-blue-100 text-blue-700",
    treasurer:   "bg-green-100 text-green-700",
    vice_chair:  "bg-purple-100 text-purple-700",
    custom:      "bg-teal-100 text-teal-700",
  };
  const Icon = ROLE_ICONS[roleType] || Zap;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${colors[roleType] || colors.custom}`}>
      <Icon size={10} />
      {roleType?.replace("_", " ")}
    </span>
  );
};

// ─── Section card ─────────────────────────────────────────────────────────────
const Section = ({ title, subtitle, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#001f3f] to-teal-600 flex items-center justify-center flex-shrink-0">
            <Icon size={15} className="text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-slate-800">{title}</p>
            {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
          </div>
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>
      {open && <div className="border-t border-slate-100">{children}</div>}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const ChelekoConfiguration = ({ gangs = [], onRefresh }) => {
  const [selectedGang, setSelectedGang]     = useState(null);
  const [gangRoles, setGangRoles]           = useState([]);
  const [approvalChain, setApprovalChain]   = useState([]);
  const [gangMembers, setGangMembers]       = useState([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [success, setSuccess]               = useState(null);
  const [editingRole, setEditingRole]       = useState(null);
  const [showAddRole, setShowAddRole]       = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(null); // roleId
  const [newRole, setNewRole]               = useState({ name: "", description: "", can_approve_service_requests: false, approval_order: "" });
  const [chainConfig, setChainConfig]       = useState([]); // working copy for drag-sort

  // ── Auto-clear messages ────────────────────────────────────────────────────
  useEffect(() => {
    if (success) { const t = setTimeout(() => setSuccess(null), 3000); return () => clearTimeout(t); }
  }, [success]);

  // ── Load data when gang selected ───────────────────────────────────────────
  useEffect(() => {
    if (selectedGang) {
      loadGangData(selectedGang.id);
    }
  }, [selectedGang]);

  // Every gang should have these; older gangs predate Walezi / Board Member.
  const DEFAULT_ROLE_TYPES = [
    { type: "member",       name: "Member" },
    { type: "chairperson",  name: "Chairperson" },
    { type: "secretary",    name: "Secretary" },
    { type: "treasurer",    name: "Treasurer" },
    { type: "walezi",       name: "Walezi (Guardians)" },
    { type: "board_member", name: "Board Member" },
  ];
  const missingDefaultRoles = DEFAULT_ROLE_TYPES.filter(
    d => !gangRoles.some(r => r.role_type === d.type)
  );

  // Holders arrive with the membership form's name; show the login account like the
  // assign pop-up does, and flag forms filled in under someone else's name.
  const holderInfo = (h) => {
    const member = gangMembers.find(m => (m.membership_id || m.id) === h.membership_id);
    const name = member?.user?.name || h.name || "Unknown";
    const formName = member?.full_name || h.name;
    const differs = formName && formName.trim().toLowerCase() !== name.trim().toLowerCase();
    return { name, email: member?.user?.email, formName: differs ? formName : null };
  };

  const loadGangData = async (gangId) => {
    setLoading(true);
    try {
      const [rolesData, chainData, membersData] = await Promise.all([
        api.get(`/gang-roles/?gang_id=${gangId}`),
        api.get(`/gang-roles/approval-chain/?gang_id=${gangId}`),
        api.get(`/community-gangs/${gangId}/`),
      ]);
      // Merge holders from approval-chain into gangRoles
      // /gang-roles/ returns roles WITHOUT holders; /approval-chain/ has full holder data
      const rawRoles = rolesData.results || rolesData;
      const allChainRoles = [
        ...(chainData.approval_chain || []),
        ...(chainData.non_chain_roles || []),
      ];
      const holdersMap = {};
      allChainRoles.forEach(r => { holdersMap[r.id] = r.holders || []; });
      // Inject holders into each role
      const rolesWithHolders = rawRoles.map(r => ({
        ...r,
        holders: holdersMap[r.id] ?? r.holders ?? [],
        has_holder: (holdersMap[r.id] ?? []).length > 0,
      }));
      setGangRoles(rolesWithHolders);
      setApprovalChain(chainData.approval_chain || []);
      // chainConfig uses approval_chain (already has holders)
      setChainConfig(chainData.approval_chain?.map(r => ({
        ...r,
        _dirty: false,
        // Ensure holders is always an array
        holders: r.holders || [],
        has_holder: (r.holders || []).length > 0,
      })) || []);
      setGangMembers(membersData.members || []);
      setError(null);
    } catch (err) {
      setError("Failed to load gang configuration.");
    } finally {
      setLoading(false);
    }
  };

  // ── Init default roles ─────────────────────────────────────────────────────
  const handleInitDefaults = async () => {
    setLoading(true);
    try {
      await api.post("/gang-roles/init-defaults/", { gang_id: selectedGang.id });
      await loadGangData(selectedGang.id);
      setSuccess("Default roles created successfully!");
    } catch (err) { setError("Failed to create default roles."); }
    finally { setLoading(false); }
  };

  // ── Create custom role ─────────────────────────────────────────────────────
  const handleCreateRole = async () => {
    if (!newRole.name.trim()) { setError("Role name is required"); return; }
    setLoading(true);
    try {
      await api.post("/gang-roles/create-custom/", {
        gang_id: selectedGang.id,
        name: newRole.name,
        description: newRole.description,
        can_approve_service_requests: newRole.can_approve_service_requests,
        approval_order: newRole.can_approve_service_requests && newRole.approval_order ? parseInt(newRole.approval_order) : null,
      });
      setNewRole({ name: "", description: "", can_approve_service_requests: false, approval_order: "" });
      setShowAddRole(false);
      await loadGangData(selectedGang.id);
      setSuccess("Custom role created!");
    } catch (err) { setError("Failed to create role."); }
    finally { setLoading(false); }
  };

  // ── Delete role ────────────────────────────────────────────────────────────
  const handleDeleteRole = async (roleId) => {
    if (!window.confirm("Delete this role? Members will lose this assignment.")) return;
    setLoading(true);
    try {
      await api.del(`/gang-roles/${roleId}/`);
      await loadGangData(selectedGang.id);
      setSuccess("Role deleted.");
    } catch (err) { setError("Failed to delete role."); }
    finally { setLoading(false); }
  };

  // ── Assign member to role ──────────────────────────────────────────────────
  const handleAssignMember = async (roleId, membershipId) => {
    setLoading(true);
    try {
      await api.post(`/gang-roles/${roleId}/assign/`, { membership_id: membershipId });
      await loadGangData(selectedGang.id);
      setShowAssignModal(null);
      setSuccess("Member assigned to role!");
    } catch (err) { setError(err?.detail || "Failed to assign member."); }
    finally { setLoading(false); }
  };

  // ── Unassign member from role ──────────────────────────────────────────────
  const handleUnassignMember = async (roleId, membershipId) => {
    setLoading(true);
    try {
      await api.post(`/gang-roles/${roleId}/unassign/`, { membership_id: membershipId });
      await loadGangData(selectedGang.id);
      setSuccess("Member unassigned.");
    } catch (err) { setError("Failed to unassign member."); }
    finally { setLoading(false); }
  };

  // ── Update chain position (move up/down) ───────────────────────────────────
  const moveChainItem = (idx, dir) => {
    const arr = [...chainConfig];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= arr.length) return;
    [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
    setChainConfig(arr.map((r, i) => ({ ...r, approval_order: i + 1, _dirty: true })));
  };

  // ── Save chain order ───────────────────────────────────────────────────────
  const handleSaveChainOrder = async () => {
    setLoading(true);
    try {
      await api.post("/gang-roles/configure-chain/", {
        gang_id: selectedGang.id,
        chain: chainConfig.map((r, i) => ({ role_id: r.id, approval_order: i + 1 })),
      });
      await loadGangData(selectedGang.id);
      setSuccess("Approval chain saved!");
    } catch (err) { setError("Failed to save chain order."); }
    finally { setLoading(false); }
  };

  // ── Toggle role in chain ───────────────────────────────────────────────────
  const handleToggleChain = async (roleId, currentlyInChain) => {
    setLoading(true);
    try {
      if (currentlyInChain) {
        await api.post("/gang-roles/configure-chain/", {
          gang_id: selectedGang.id,
          remove_from_chain: [roleId],
        });
      } else {
        // Add to end of chain
        const maxOrder = Math.max(0, ...approvalChain.map(r => r.approval_order || 0));
        await api.patch(`/gang-roles/${roleId}/update-chain-position/`, {
          approval_order: maxOrder + 1,
          can_approve_service_requests: true,
        });
      }
      await loadGangData(selectedGang.id);
      setSuccess(currentlyInChain ? "Role removed from approval chain." : "Role added to approval chain.");
    } catch (err) { setError("Failed to update chain."); }
    finally { setLoading(false); }
  };

  // ── Computed ───────────────────────────────────────────────────────────────
  const nonChainRoles = gangRoles.filter(r =>
    !approvalChain.some(c => c.id === r.id)
  );
  const chainDirty = chainConfig.some(r => r._dirty);

  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6">

      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-800">Configuration</h2>
          <p className="text-xs text-slate-400 mt-0.5">Manage gang roles, approval chains, and settings</p>
        </div>
        {selectedGang && (
          <button onClick={() => loadGangData(selectedGang.id)} className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-xl px-3 py-2 hover:bg-slate-50">
            <RefreshCw size={13} /> Refresh
          </button>
        )}
      </div>

      {/* Toast messages */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          <AlertTriangle size={15} />
          {error}
          <button onClick={() => setError(null)} className="ml-auto"><X size={13} /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 text-teal-700 px-4 py-3 rounded-xl text-sm">
          <Check size={15} />
          {success}
        </div>
      )}

      {/* ── Gang selector ───────────────────────────────────────────────── */}
      <Section title="Select Community" subtitle="Choose which gang to configure" icon={Settings} defaultOpen={true}>
        <div className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {gangs.map(gang => (
              <button
                key={gang.id}
                onClick={() => setSelectedGang(gang)}
                className={`flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                  selectedGang?.id === gang.id
                    ? "border-teal-400 bg-teal-50 shadow-sm"
                    : "border-slate-100 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  selectedGang?.id === gang.id
                    ? "bg-gradient-to-br from-teal-500 to-teal-600"
                    : "bg-gradient-to-br from-[#001f3f] to-teal-700"
                }`}>
                  <span className="text-white text-xs font-bold">{gang.name?.[0]?.toUpperCase()}</span>
                </div>
                <div className="min-w-0">
                  <p className={`text-xs font-semibold truncate ${selectedGang?.id === gang.id ? "text-teal-700" : "text-slate-700"}`}>{gang.name}</p>
                  <p className="text-xs text-slate-400">{gang.member_count || 0} members</p>
                </div>
                {selectedGang?.id === gang.id && <Check size={14} className="text-teal-500 flex-shrink-0 ml-auto" />}
              </button>
            ))}
          </div>
          {gangs.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No communities found</p>}
        </div>
      </Section>

      {/* ── Role configuration (only when gang selected) ────────────────── */}
      {selectedGang && !loading && (
        <>
          {/* Quick actions bar */}
          <div className="flex items-center gap-3 bg-gradient-to-r from-[#001f3f] to-teal-700 rounded-2xl p-4">
            <Shield size={20} className="text-teal-300 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-white">{selectedGang.name}</p>
              <p className="text-xs text-teal-200">{gangRoles.length} roles configured · {approvalChain.length} in approval chain</p>
            </div>
            <div className="flex gap-2 ml-auto">
              <button onClick={handleInitDefaults}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-100 text-[#001f3f] text-xs font-semibold rounded-xl transition-colors">
                <Zap size={13} />
                Init Defaults
              </button>
              <button onClick={() => setShowAddRole(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-400 hover:bg-teal-300 text-white text-xs font-medium rounded-xl transition-colors">
                <Plus size={13} />
                Custom Role
              </button>
            </div>
          </div>

          {/* Missing default roles (e.g. Walezi, Board Member on older gangs) */}
          {missingDefaultRoles.length > 0 && (
            <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <AlertTriangle size={18} className="text-amber-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">
                  {missingDefaultRoles.length} default role{missingDefaultRoles.length === 1 ? "" : "s"} missing
                </p>
                <p className="text-xs text-amber-700">
                  {missingDefaultRoles.map(r => r.name).join(", ")} — add {missingDefaultRoles.length === 1 ? "it" : "them"} to use the full approval chain.
                </p>
              </div>
              <button onClick={handleInitDefaults}
                className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-xl transition-colors flex-shrink-0">
                <Zap size={13} />
                Add missing roles
              </button>
            </div>
          )}


          {/* ── Roles list ─────────────────────────────────────────────── */}
          <Section title="Gang Roles" subtitle="All roles defined for this community" icon={Users}>
            <div className="p-5 space-y-2">
              {gangRoles.map(role => {
                const inChain = approvalChain.some(c => c.id === role.id);
                const chainStep = approvalChain.find(c => c.id === role.id);
                const Icon = ROLE_ICONS[role.role_type] || Zap;

                return (
                  <div key={role.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-slate-200 transition-all">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      inChain
                        ? "bg-gradient-to-br from-teal-500 to-teal-600"
                        : "bg-slate-200"
                    }`}>
                      <Icon size={16} className={inChain ? "text-white" : "text-slate-500"} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-slate-800">{role.name}</p>
                        <RoleBadge roleType={role.role_type} />
                        {inChain && (
                          <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">
                            Step {chainStep?.approval_order}
                          </span>
                        )}
                        {!role.is_active && (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs font-medium rounded-full">Inactive</span>
                        )}
                      </div>
                      {role.description && <p className="text-xs text-slate-400 mt-0.5 truncate">{role.description}</p>}

                      {/* Holders */}
                      {role.holders?.length > 0 && (
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                          {role.holders.filter(h => h).map((h, i) => {
                            const info = holderInfo(h);
                            return (
                              <div key={i} className="flex items-center gap-1 bg-white border border-slate-200 rounded-full px-2 py-0.5">
                                <div className="w-4 h-4 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-[9px] font-bold text-teal-600">{info.name[0]?.toUpperCase()}</span>
                                </div>
                                <span className="text-xs text-slate-600">{info.name}</span>
                                {info.email && <span className="text-[10px] text-slate-400">{info.email}</span>}
                                {info.formName && (
                                  <span className="text-[10px] font-medium text-amber-700 bg-amber-50 rounded px-1" title="The membership form was filled in with a different name than the login account">
                                    Form: {info.formName}
                                  </span>
                                )}
                                <button onClick={() => handleUnassignMember(role.id, h.membership_id)} className="ml-0.5 text-slate-300 hover:text-red-400 transition-colors">
                                  <X size={10} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {/* Toggle in chain */}
                      <button
                        onClick={() => handleToggleChain(role.id, inChain)}
                        title={inChain
                          ? "This role is in the approval chain — click to remove it"
                          : "This role is not in the approval chain — click to add it"}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-medium transition-all ${
                          inChain
                            ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                            : "bg-teal-100 text-teal-700 hover:bg-teal-200"
                        }`}
                      >
                        {inChain ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                        {inChain ? "Remove from chain" : "Add to chain"}
                      </button>

                      {/* Assign member */}
                      <button
                        onClick={() => setShowAssignModal(role.id)}
                        className="p-1.5 hover:bg-blue-50 text-blue-500 rounded-lg transition-colors"
                        title="Assign member"
                      >
                        <UserCheck size={14} />
                      </button>

                      {/* Delete (only custom) */}
                      {role.role_type === "custom" && (
                        <button onClick={() => handleDeleteRole(role.id)} className="p-1.5 hover:bg-red-50 text-red-400 rounded-lg transition-colors" title="Delete role">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              {gangRoles.length === 0 && (
                <div className="py-10 text-center">
                  <Shield size={32} className="mx-auto mb-3 text-slate-200" />
                  <p className="text-sm text-slate-400 mb-3">No roles configured yet</p>
                  <button onClick={handleInitDefaults} className="px-4 py-2 bg-gradient-to-r from-[#001f3f] to-teal-600 text-white text-sm rounded-xl hover:opacity-90">
                    Create Default Roles
                  </button>
                </div>
              )}
            </div>
          </Section>

          {/* ── Approval chain order ────────────────────────────────────── */}
          {approvalChain.length > 0 && (
            <Section title="Approval Chain Order" subtitle="Service requests must pass through these steps before admin approval" icon={ChevronRight}>
              <div className="p-5">
                <div className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <Info size={15} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    Use the arrows to reorder steps. Admin approval is always the final step and cannot be moved.
                    Click <strong>Save Order</strong> to apply changes.
                  </p>
                </div>

                <div className="space-y-2">
                  {chainConfig.map((role, idx) => {
                    const Icon = ROLE_ICONS[role.role_type] || Zap;
                    return (
                      <div key={role.id} className={`flex items-center gap-3 p-3.5 rounded-xl border ${role._dirty ? "border-teal-200 bg-teal-50" : "border-slate-100 bg-white"} transition-all`}>
                        <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#001f3f] to-teal-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-bold">{idx + 1}</span>
                        </div>
                        <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <Icon size={14} className="text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800">{role.name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            {role.holders?.map((h, i) => {
                              if (!h) return null;
                              const info = holderInfo(h);
                              return (
                                <span key={i} className="inline-flex items-center gap-1 text-xs text-slate-600 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
                                  <span className="w-3.5 h-3.5 rounded-full bg-teal-100 flex items-center justify-center text-[8px] font-bold text-teal-700 flex-shrink-0">{info.name[0]?.toUpperCase()}</span>
                                  {info.name}
                                  {info.email && <span className="text-[10px] text-slate-400">{info.email}</span>}
                                  {info.formName && (
                                    <span className="text-[10px] font-medium text-amber-700 bg-amber-50 rounded px-1" title="The membership form was filled in with a different name than the login account">
                                      Form: {info.formName}
                                    </span>
                                  )}
                                  <button onClick={() => handleUnassignMember(role.id, h.membership_id)} className="ml-0.5 text-slate-300 hover:text-red-400 transition-colors" title="Unassign">
                                    <X size={9} />
                                  </button>
                                </span>
                              );
                            })}
                            {(!role.holders || role.holders.length === 0) && (
                              <span className="text-xs text-amber-600 flex items-center gap-1 font-medium">
                                <AlertTriangle size={10} /> No member assigned
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={() => moveChainItem(idx, -1)} disabled={idx === 0} className="p-1.5 hover:bg-slate-100 text-slate-400 disabled:opacity-20 rounded-lg transition-colors"><ArrowUp size={13} /></button>
                          <button onClick={() => moveChainItem(idx, 1)} disabled={idx === chainConfig.length - 1} className="p-1.5 hover:bg-slate-100 text-slate-400 disabled:opacity-20 rounded-lg transition-colors"><ArrowDown size={13} /></button>
                        </div>
                      </div>
                    );
                  })}

                  {/* Admin step indicator */}
                  <div className="flex items-center gap-3 p-3.5 rounded-xl border border-dashed border-slate-200 bg-slate-50">
                    <div className="w-7 h-7 rounded-lg bg-slate-300 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">{chainConfig.length + 1}</span>
                    </div>
                    <div className="w-7 h-7 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                      <Lock size={14} className="text-slate-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-500">System Admin</p>
                      <p className="text-xs text-slate-400">Final approval — always last</p>
                    </div>
                    <span className="ml-auto text-xs bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">Fixed</span>
                  </div>
                </div>

                {chainDirty && (
                  <div className="mt-4 flex justify-end">
                    <button onClick={handleSaveChainOrder} disabled={loading}
                      className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-gradient-to-r from-[#001f3f] to-teal-600 text-white rounded-xl hover:opacity-90 disabled:opacity-60">
                      <Save size={14} />
                      Save Order
                    </button>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* ── Gang settings summary ───────────────────────────────────── */}
          <Section title="Community Settings" subtitle="Current configuration overview" icon={Info} defaultOpen={false}>
            <div className="p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Joining Fee", value: `TZS ${parseFloat(selectedGang.joining_fee || 0).toLocaleString()}` },
                  { label: "Type", value: selectedGang.is_private ? "Private" : "Public" },
                  { label: "Specification", value: selectedGang.specification || "All" },
                  { label: "Total Members", value: selectedGang.member_count || 0 },
                  { label: "Contact Email", value: selectedGang.contact_email || "—" },
                  { label: "Contact Phone", value: selectedGang.contact_phone || "—" },
                  { label: "Location", value: selectedGang.location || "—" },
                  { label: "Roles", value: gangRoles.length },
                ].map(item => (
                  <div key={item.label} className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-400 mb-1">{item.label}</p>
                    <p className="text-sm font-semibold text-slate-700 truncate">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </Section>

        </>
      )}

      {/* Loading state */}
      {loading && selectedGang && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-teal-600">
            <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium">Loading configuration...</span>
          </div>
        </div>
      )}

      {/* No gang selected */}
      {!selectedGang && (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-16 text-center">
          <Settings size={40} className="mx-auto mb-4 text-slate-200" />
          <p className="text-slate-400 text-sm">Select a community above to configure its roles and approval chain</p>
        </div>
      )}

      {/* ── ADD CUSTOM ROLE MODAL ──────────────────────────────────────── */}
      {showAddRole && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-800">Create Custom Role</h3>
              <button onClick={() => setShowAddRole(false)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><X size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Role Name *</label>
                <input type="text" value={newRole.name} onChange={e => setNewRole({ ...newRole, name: e.target.value })}
                  placeholder="e.g., Mkaguzi (Auditor)"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Description</label>
                <textarea value={newRole.description} onChange={e => setNewRole({ ...newRole, description: e.target.value })}
                  rows={2} placeholder="Describe this role's responsibilities"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none" />
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                <input type="checkbox" id="can_approve" checked={newRole.can_approve_service_requests}
                  onChange={e => setNewRole({ ...newRole, can_approve_service_requests: e.target.checked, approval_order: e.target.checked ? newRole.approval_order : "" })}
                  className="w-4 h-4 text-teal-600 rounded" />
                <label htmlFor="can_approve" className="flex-1">
                  <p className="text-sm font-medium text-slate-700">Include in approval chain</p>
                  <p className="text-xs text-slate-400">This role will be required to approve service requests</p>
                </label>
              </div>
              {newRole.can_approve_service_requests && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Approval Order</label>
                  <input type="number" value={newRole.approval_order} onChange={e => setNewRole({ ...newRole, approval_order: e.target.value })}
                    placeholder="e.g., 4 (after treasurer)"
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300" />
                  <p className="text-xs text-slate-400 mt-1">Current chain has {approvalChain.length} steps. Leave empty to add at end.</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
              <button onClick={() => setShowAddRole(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50">Cancel</button>
              <button onClick={handleCreateRole} disabled={loading}
                className="px-5 py-2 text-sm font-medium bg-gradient-to-r from-[#001f3f] to-teal-600 text-white rounded-xl hover:opacity-90 disabled:opacity-60">
                Create Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── ASSIGN MEMBER MODAL ────────────────────────────────────────── */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-800">Assign Member to Role</h3>
              <button onClick={() => setShowAssignModal(null)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><X size={16} /></button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto">
              {gangMembers.length > 0 ? (
                <div className="space-y-2">
                  {gangMembers.map(m => (
                    <button
                      key={m.membership_id || m.id}
                      onClick={() => handleAssignMember(showAssignModal, m.membership_id || m.id)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-teal-50 hover:border-teal-200 border border-slate-100 rounded-xl transition-all text-left"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#001f3f] to-teal-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs font-bold">{(m.user?.name || m.full_name)?.[0]?.toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{m.user?.name || m.full_name || "Unknown"}</p>
                        <p className="text-xs text-slate-400">{m.user?.email || "—"}</p>
                      </div>
                      <ChevronRight size={14} className="text-slate-300 ml-auto" />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <Users size={28} className="mx-auto mb-2 text-slate-200" />
                  <p className="text-sm text-slate-400">No approved members found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ChelekoConfiguration;